import * as React from 'react'
import * as Papa from 'papaparse'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import {
  PaginatedPayment,
  PaymentFilter,
  API,
  ILocation,
  IPayment,
  IBaseProduct,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { message, Skeleton } from 'antd'
import { AppView, AppMainView } from '../../../../components/AppView/index'
import AppHeader from '../../../../components/AppView/Header'
import { PaymentDetailsModal } from './PaymentDetailsModal'
import { browserHistory } from 'react-router'
import AppPagination from '../../../../components/Pagination/index'
import { PaymentTable } from './PaymentTable'
import { FilterGroup } from './FilterGroup'
import { SalesExportModal } from './SalesExportModal'
import { NoPermissionsResult } from '../../../../components/NoPermissionsResult'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import './index.scss'
import { ManualPaymentModal } from './ManualPaymentModal'
import { dayjs } from '../../../../utilities/dayjs'
import { CompaniesModels } from '@getgreenline/companies'
import { BlazepayEnums, BlazepayPaymentOptionModels } from '@getgreenline/payments'
import { getPaymentOptions } from '../../../../utilities/paymentsHelper'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  location?: any
}

interface State {
  isLoading: boolean
  paginatedPayments?: PaginatedPayment
  selectedProducts: Array<{ id: string; name: string }>
  selectedLocation?: ILocation
  userPermittedLocations: ILocation[]
  showPaymentModal: boolean
  isExportModalOpen: boolean
  showManualPaymentModal: boolean
  paymentOptions?: Map<
    BlazepayEnums.Provider,
    BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
  >
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class SalesList extends React.Component<Props, State> {
  filter = new PaymentFilter(this.props.currentCompanyStore!.company!.id, 20, 0)

  incomingPayment?: IPayment

  constructor(props: Props) {
    super(props)

    this.state = {
      isLoading: false,
      selectedProducts: [],
      userPermittedLocations: [],
      showPaymentModal: true,
      isExportModalOpen: false,
      showManualPaymentModal: false,
    }
    const query = browserHistory.getCurrentLocation().query
    query.startDate
      ? this.filter.setStartDate(dayjs(query.startDate))
      : this.filter.setStartDate(dayjs().subtract(2, 'weeks').startOf('day'))
    query.endDate
      ? this.filter.setEndDate(dayjs(query.endDate))
      : this.filter.setEndDate(dayjs().endOf('day'))
    if (query.showOnlineDiscrepancies) {
      this.filter.setShowOnlineDiscrepancies(query.showOnlineDiscrepancies)
    }
  }

  async componentDidMount() {
    const currentCompanyStore = this.props.currentCompanyStore!
    currentCompanyStore!.getDevices()
    currentCompanyStore!.getEntityUsers()
    await this.getLocations()
    const { location } = this.props

    if (location && location.query && location.query.paymentId) {
      const incomingPaymentId = location.query.paymentId
      this.incomingPayment = await this.getPaymentById(
        currentCompanyStore!.company!.id,
        incomingPaymentId,
      )
    }

    this.state.selectedLocation &&
      (await this.props.currentCompanyStore?.setCustomReceiptSetting(
        this.state.selectedLocation.id,
      ))

    await this.setPaymentOptions()
  }

  setPaymentOptions = async () => {
    const companyId = this.props.currentCompanyStore?.company?.id
    const locationId = this.filter.locationIds?.[0]
    if (!locationId || !companyId) return

    const paymentOptions = await getPaymentOptions(companyId, locationId)
    this.setState({ paymentOptions })
  }

  async getPaymentById(companyId: number, paymentId: string): Promise<IPayment> {
    const payment = await API.getPaymentById(companyId, paymentId)
    return payment
  }

  async getLocations() {
    const currentCompanyStore = this.props.currentCompanyStore!
    const locations = await currentCompanyStore!.getLocations()

    if (locations.length > 0) {
      const userPermittedLocations = locations.filter((location) => {
        return this.props.currentUserStore!.hasPermissionAtEntity(
          PermissionNames.CAN_VIEW_SALES,
          location.id,
        )
      })

      const firstUserPermittedLocation = userPermittedLocations[0]
      if (firstUserPermittedLocation) {
        browserHistory.getCurrentLocation().query.showOnlineDiscrepancies
          ? (this.filter.locationIds = undefined)
          : (this.filter.locationIds = [firstUserPermittedLocation.id])

        this.setState(
          {
            userPermittedLocations,
            selectedLocation: firstUserPermittedLocation,
          },
          this.search,
        )
      }
    }
  }

  // Only call this after setting the state
  // Remember that this.setState is asynchronous
  search = async () => {
    if (!(this.filter.locationIds && this.filter.locationIds.length > 0)) {
      message.info('Please select location to display payments')
      return
    }

    await this.setState({ isLoading: true })
    const paginatedPayments = await this.props.currentCompanyStore!.getLocationPayments(
      this.filter.locationIds[0],
      this.filter,
    )

    this.state.selectedLocation &&
      (await this.props.currentCompanyStore?.setCustomReceiptSetting(
        this.state.selectedLocation.id,
      ))

    this.setState({ paginatedPayments })

    await this.setState({ isLoading: false })
  }

  setOffset(newOffset: number) {
    this.filter.setOffset(newOffset)
    this.search()
  }

  setLocation = async (companyId: number, locationId: number) => {
    const locationResult = await API.getLocationById(companyId, locationId)
    this.filter.locationIds = [locationResult.id]
    this.setState({ selectedLocation: locationResult })
  }

  setProduct = async (product: IBaseProduct, name: string) => {
    // duplicate id
    if (this.filter.productIds.includes(product.id)) {
      return
    }

    const newProductIds = [...this.filter.productIds, product.id]
    const newProducts = [...this.state.selectedProducts, { id: product.id, name }]

    await this.setState({ selectedProducts: newProducts })
    this.filter.setProductIds(newProductIds)
  }

  removeSelectedProducts = async (product: { id: string; name: string }): Promise<void> => {
    await this.setState({
      selectedProducts: this.state.selectedProducts.filter((p) => p.id !== product.id),
    })

    this.filter.setProductIds(this.state.selectedProducts.map((p) => p.id))
  }

  /**
   * Do we need to keep local state here for selectedProducts? maybe refactor
   * filter state / logic, look into use context instead
   */
  removeAllProducts = async (): Promise<void> => {
    await this.setState({
      selectedProducts: [],
    })

    this.filter.setProductIds([])
  }

  _export(fileName: string, json: any) {
    const csvString = Papa.unparse(json)

    // Reference: https://github.com/mholt/PapaParse/issues/175
    const blob = new Blob([csvString])
    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, fileName + '.csv')
    } else {
      const a = window.document.createElement('a')
      a.href = (window.URL as any).createObjectURL(blob, { type: 'text/plain' } as any)
      a.download = fileName + '.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  toggleExportModal = () => this.setState({ isExportModalOpen: !this.state.isExportModalOpen })

  get currentPage(): number {
    const { paginatedPayments } = this.state

    if (!paginatedPayments) {
      return 0
    }

    const currPage = paginatedPayments.offset / paginatedPayments.limit

    return isNaN(currPage) ? 0 : currPage
  }

  get numberOfPages(): number {
    const { paginatedPayments } = this.state

    if (!paginatedPayments) {
      return 0
    }

    const pageCount = Math.ceil(paginatedPayments.total / paginatedPayments.limit)

    return isNaN(pageCount) ? 0 : pageCount
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const canViewSales = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_SALES,
      company.id,
    )
    const canEditSales = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_SALES,
      company.id,
    )
    const canRefundSales = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_REFUND_SALES,
      company.id,
    )
    const canEditCustomers = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_CUSTOMERS,
      company.id,
    )

    const canPerformManualSale =
      (company?.province &&
        [
          CompaniesModels.Province.SK,
          CompaniesModels.Province.AB,
          CompaniesModels.Province.MB,
        ].includes(
          company.province as CompaniesModels.Province, // bad type support for strings
        )) ||
      this.props.currentUserStore!.isAdmin

    if (!canViewSales) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_SALES} />
    }

    const {
      isLoading,
      paginatedPayments,
      userPermittedLocations,
      selectedLocation,
      selectedProducts,
      showPaymentModal,
      isExportModalOpen,
    } = this.state

    return (
      <AppView column>
        <Helmet>
          <title>Sales | Sales List</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <AppHeader title='Sales'>
          <BlazeButton
            buttonProps={{
              icon: 'download',
              onClick: this.toggleExportModal,
              disabled: isExportModalOpen,
              className: 'mr-2',
            }}
            titleProps={{
              title: 'Export',
            }}
          />
          {canPerformManualSale && (
            <BlazeButton
              buttonProps={{
                icon: 'plus',
                type: 'primary',
                disabled: !this.props.currentUserStore!.hasPermissionAtEntity(
                  PermissionNames.CAN_EDIT_SALES,
                  company.id,
                ),
                onClick: () => this.setState({ showManualPaymentModal: true }),
              }}
              titleProps={{
                title: 'Add manual sale',
              }}
            />
          )}
        </AppHeader>

        <AppMainView>
          <div className='row'>
            <div className='col-md-12'>
              <FilterGroup
                filter={this.filter}
                selectedLocation={selectedLocation}
                userPermittedLocations={userPermittedLocations}
                setLocation={this.setLocation}
                search={this.search}
                setProduct={this.setProduct}
                selectedProducts={selectedProducts}
                removeSelectedProduct={this.removeSelectedProducts}
                removeAllProducts={this.removeAllProducts}
              />
            </div>
          </div>
          <br />
          {isLoading || paginatedPayments === undefined ? (
            <Skeleton active />
          ) : paginatedPayments.payments.length === 0 ? (
            <div className='mb-2'>
              <p className='text-muted'>No sales have been processed yet</p>
            </div>
          ) : (
            <PaymentTable
              paginatedPayments={paginatedPayments}
              currentEmployeeId={this.props.currentUserStore!.currentUser!.id}
              search={this.search}
              canEdit={canEditSales}
              canRefund={canRefundSales}
              canEditCustomer={canEditCustomers}
              paymentOptions={this.state.paymentOptions}
            />
          )}
          <AppPagination
            currentPage={this.currentPage}
            pageCount={this.numberOfPages}
            onPageChange={({ selected }: { selected: number }) => {
              this.setOffset(selected * this.filter.limit)
            }}
          />
        </AppMainView>

        {/* sale modal */}
        {this.incomingPayment && showPaymentModal && (
          <PaymentDetailsModal
            payment={this.incomingPayment}
            canEditSales={canEditSales}
            canRefundSales={canRefundSales}
            canEditCustomer={canEditCustomers}
            locationId={this.filter!.locationIds ? this.filter.locationIds[0] : undefined}
            currentEmployeeId={this.props.currentUserStore!.currentUser!.id}
            onClose={() => {
              this.setState({ showPaymentModal: false })
              this.getLocations()
              browserHistory.push({
                pathname: this.props.location.pathname,
                query: {},
              })
            }}
          />
        )}

        {isExportModalOpen && (
          <SalesExportModal
            _export={this._export}
            filter={this.filter}
            toggle={this.toggleExportModal}
          />
        )}

        {this.state.showManualPaymentModal && (
          <ManualPaymentModal
            onClose={() => this.setState({ showManualPaymentModal: false })}
            onSubmit={() => {
              this.setState({ showManualPaymentModal: false })
              this.search()
            }}
          />
        )}
      </AppView>
    )
  }
}
